"use client"

import { useState, useEffect } from "react"
import { ref, set, update, remove, onValue } from "../lib/firebase-database"
import { db } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import { useToast } from "../components/ui/use-toast"
import { Layout } from "./Layout"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Switch } from "./ui/switch"
import { Check, Trash2, MoveUp, MoveDown, Plus, ImagePlus, Loader2, X } from "lucide-react"
import { destinations as predefinedDestinations } from "../config/destinations"

const DestinationsPage = () => {
  const { isAdmin } = useAuth()
  const { toast } = useToast()
  const [destinations, setDestinations] = useState([])
  const [newDestination, setNewDestination] = useState({
    name: "",
    image: null,
    imagePreview: "",
    imageLink: "", 
    order: 0,
    isVisible: true,
  })
  const [editingId, setEditingId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (!isAdmin) return

    const destinationsRef = ref(db, "destinations")
    onValue(destinationsRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const destinationList = Object.entries(data).map(([id, value]) => ({
          id,
          ...value,
        }))
        setDestinations(destinationList.sort((a, b) => (a.order || 0) - (b.order || 0)))
      } else {
        setDestinations([])
      }
      setIsLoading(false)
    })
  }, [isAdmin])

  const handleImageChange = async (e, id = null) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Image size should be less than 5MB",
      })
      return
    }

    if (id) {
      // Edit existing destination
      const updatedDestinations = destinations.map((destination) => {
        if (destination.id === id) {
          return {
            ...destination,
            imageFile: file,
            imagePreview: URL.createObjectURL(file),
          }
        }
        return destination
      })
      setDestinations(updatedDestinations)
    } else {
      // New destination
      setNewDestination({
        ...newDestination,
        image: file,
        imagePreview: URL.createObjectURL(file),
      })
    }
  }

  const handleAddOrUpdate = async (e) => {
    e.preventDefault()

    if (!newDestination.name) {
      toast({
        variant: "destructive", 
        title: "Error",
        description: "Destination name is required"
      })
      return
    }

    setUploading(true)
    try {
      // Find matching predefined destination
      const predefinedDest = predefinedDestinations.find(
        d => d.name.toLowerCase() === newDestination.name.toLowerCase()
      )

      const destinationData = {
        name: newDestination.name,
        imageUrl: predefinedDest?.imageUrl || newDestination.imageLink,
        order: newDestination.order || destinations.length,
        isVisible: newDestination.isVisible,
        createdAt: editingId
          ? destinations.find((d) => d.id === editingId)?.createdAt || new Date().toISOString()
          : new Date().toISOString(),
      }

      const destinationRef = ref(db, `destinations/${editingId || Date.now()}`)

      if (editingId) {
        await update(destinationRef, destinationData)
        toast({
          title: "Success",
          description: "Destination updated successfully",
        })
        setEditingId(null)
      } else {
        await set(destinationRef, destinationData)
        toast({
          title: "Success", 
          description: "Destination added successfully"
        })
      }

      setNewDestination({
        name: "",
        image: null,
        imagePreview: "",
        imageLink: "",
        order: 0,
        isVisible: true,
      })
    } catch (error) {
      console.error("Error saving destination:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save destination" 
      })
    } finally {
      setUploading(false)
    }
  }

  const handleEdit = (destination) => {
    setEditingId(destination.id)
    setNewDestination({
      name: destination.name,
      image: null,
      imagePreview: destination.imageUrl,
      order: destination.order || 0,
      isVisible: destination.isVisible,
    })
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this destination?")) return

    try {
      await remove(ref(db, `destinations/${id}`))
      toast({
        title: "Success",
        description: "Destination deleted successfully",
      })
    } catch (error) {
      console.error("Error deleting destination:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete destination",
      })
    }
  }

  const handleToggleVisibility = async (id, currentVisibility) => {
    try {
      await update(ref(db, `destinations/${id}`), { isVisible: !currentVisibility })
      toast({
        title: "Success",
        description: `Destination ${currentVisibility ? "hidden" : "shown"} successfully`,
      })
    } catch (error) {
      console.error("Error toggling visibility:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to toggle visibility",
      })
    }
  }

  const handleUpdateOrder = async (id, direction) => {
    const currentIndex = destinations.findIndex((d) => d.id === id)
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1

    if (newIndex < 0 || newIndex >= destinations.length) return

    const reorderedDestinations = [...destinations]
    const [moved] = reorderedDestinations.splice(currentIndex, 1)
    reorderedDestinations.splice(newIndex, 0, moved)

    try {
      const updates = {}
      reorderedDestinations.forEach((destination, index) => {
        updates[`destinations/${destination.id}/order`] = index
      })
      await set(ref(db), updates)
      setDestinations(reorderedDestinations)
      toast({
        title: "Success",
        description: "Order updated successfully",
      })
    } catch (error) {
      console.error("Error updating order:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update order",
      })
    }
  }

  const handleUpdateDestination = async (id) => {
    const destination = destinations.find((d) => d.id === id)
    if (!destination) return

    setUploading(true)
    try {
      // Find matching predefined destination
      const predefinedDest = predefinedDestinations.find(
        d => d.name.toLowerCase() === destination.name.toLowerCase()
      )

      // Update the destination in Firebase
      await update(ref(db, `destinations/${id}`), {
        name: destination.name,
        imageUrl: predefinedDest?.imageUrl || destination.imageUrl,
        isVisible: destination.isVisible,
        order: destination.order,
      })

      toast({
        title: "Success",
        description: "Destination updated successfully",
      })

      // Update local state
      const updatedDestinations = destinations.map((d) =>
        d.id === id ? {
          ...d,
          imagePreview: predefinedDest?.imageUrl || d.imageUrl
        } : d
      )
      setDestinations(updatedDestinations)

    } catch (error) {
      console.error("Error updating destination:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update destination",
      })
    } finally {
      setUploading(false)
    }
  }

  const removeImage = (id = null) => {
    if (id) {
      // Remove image from existing destination
      const updatedDestinations = destinations.map((destination) => {
        if (destination.id === id) {
          return {
            ...destination,
            imageFile: null,
            imagePreview: "",
          }
        }
        return destination
      })
      setDestinations(updatedDestinations)
    } else {
      // Remove image from new destination
      setNewDestination({
        ...newDestination,
        image: null,
        imagePreview: "",
      })
    }
  }

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to access this page.</p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-2xl font-bold mb-6">Destinations Management</h1>

        {/* Add New Destination Form */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h2 className="text-xl font-semibold mb-4">{editingId ? "Edit Destination" : "Add New Destination"}</h2>
          <form onSubmit={handleAddOrUpdate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Destination Name</label>
              <select
                value={newDestination.name}
                onChange={(e) => {
                  const selected = predefinedDestinations.find(d => d.name === e.target.value)
                  setNewDestination({
                    ...newDestination,
                    name: e.target.value,
                    imageLink: selected?.imageUrl || ""
                  })
                }}
                className="w-full border rounded-md p-2"
                required
              >
                <option value="">Select a destination</option>
                {predefinedDestinations.map((dest) => (
                  <option key={dest.id} value={dest.name}>
                    {dest.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Show selected image preview */}
            {newDestination.imageLink && (
              <div className="mt-2 relative inline-block">
                <img
                  src={newDestination.imageLink}
                  alt="Preview"
                  className="h-40 w-60 object-cover rounded-lg"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Destination Image</label>
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById("destinationImage").click()}
                >
                  <ImagePlus className="h-4 w-4 mr-2" />
                  Select Image
                </Button>
                <input
                  id="destinationImage"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e)}
                  className="hidden"
                />
              </div>
              {/* Add link input for ImageKit */}
              <div className="mt-2">
                <Input
                  type="url"
                  value={newDestination.imageLink}
                  onChange={(e) => setNewDestination({ ...newDestination, imageLink: e.target.value })}
                  placeholder="Paste ImageKit link here"
                  className="w-full"
                />
              </div>
              {newDestination.imagePreview && (
                <div className="mt-2 relative inline-block">
                  <img
                    src={newDestination.imagePreview || "/placeholder.svg"}
                    alt="Preview"
                    className="h-40 w-60 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage()}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              {/* Show preview for ImageKit link if provided */}
              {newDestination.imageLink && (
                <div className="mt-2 relative inline-block">
                  <img
                    src={newDestination.imageLink}
                    alt="Preview"
                    className="h-40 w-60 object-cover rounded-lg"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Order</label>
              <Input
                type="number"
                value={newDestination.order}
                onChange={(e) => setNewDestination({ ...newDestination, order: Number.parseInt(e.target.value) || 0 })}
                placeholder="Display order"
                className="w-full"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={newDestination.isVisible}
                onCheckedChange={(checked) => setNewDestination({ ...newDestination, isVisible: checked })}
              />
              <label className="text-sm font-medium">Visible to users</label>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={uploading}>
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {editingId ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {editingId ? "Update Destination" : "Add Destination"}
                  </>
                )}
              </Button>

              {editingId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingId(null)
                    setNewDestination({
                      name: "",
                      image: null,
                      imagePreview: "",
                      order: 0,
                      isVisible: true,
                    })
                  }}
                >
                  Cancel Edit
                </Button>
              )}
            </div>
          </form>
        </div>

        {/* Existing Destinations List */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Existing Destinations</h2>

          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : destinations.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No destinations found. Add your first destination above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {destinations.map((destination, index) => (
                <div
                  key={destination.id}
                  className={`border rounded-lg overflow-hidden ${
                    destination.isVisible ? "bg-gray-50" : "bg-gray-100"
                  }`}
                >
                  <div className="relative">
                    <img
                      src={destination.imagePreview || destination.imageUrl || "/placeholder.svg"}
                      alt={destination.name}
                      className={`w-full h-48 object-cover ${!destination.isVisible && "opacity-50"}`}
                    />
                    <div className="absolute top-2 right-2 flex gap-2">
                      <Switch
                        checked={destination.isVisible}
                        onCheckedChange={() => handleToggleVisibility(destination.id, destination.isVisible)}
                        className="bg-white"
                      />
                    </div>
                    <div className="absolute top-2 left-2">
                      <span className="bg-black text-white px-2 py-1 rounded text-sm">
                        Order: {destination.order || 0}
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <Input
                      value={destination.name}
                      onChange={(e) => {
                        const updatedDestinations = destinations.map((d) =>
                          d.id === destination.id ? { ...d, name: e.target.value } : d,
                        )
                        setDestinations(updatedDestinations)
                      }}
                      className="mb-3 font-medium"
                    />

                    <div className="flex items-center gap-2 mb-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById(`editImage-${destination.id}`).click()}
                      >
                        <ImagePlus className="h-4 w-4 mr-1" />
                        Change Image
                      </Button>
                      <input
                        id={`editImage-${destination.id}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, destination.id)}
                        className="hidden"
                      />
                      {destination.imagePreview && destination.imagePreview !== destination.imageUrl && (
                        <button onClick={() => removeImage(destination.id)} className="text-red-500 hover:text-red-700">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateOrder(destination.id, "up")}
                          disabled={index === 0}
                        >
                          <MoveUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateOrder(destination.id, "down")}
                          disabled={index === destinations.length - 1}
                        >
                          <MoveDown className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(destination)}>
                          Edit
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleUpdateDestination(destination.id)}
                          disabled={uploading}
                        >
                          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(destination.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default DestinationsPage
